import { useEffect, useState } from "react";
import * as signalR from "@microsoft/signalr";
import * as httpService from "services/httpService";
import { MessageType, SignalRMessage } from "types/signalR";

type SignalRProps = {
  identifier?: string;
  url: string;
  onMessage?: (data: SignalRMessage) => void;
};

/**
 * Custom React hook for establishing and managing a SignalR connection.
 *
 * This hook handles the negotiation, connection lifecycle, and message handling for SignalR.
 * It automatically negotiates the connection URL and access token, starts the connection,
 * registers event handlers for all message types, and provides cleanup on unmount.
 *
 * @param {string} url - The negotiate endpoint URL for SignalR.
 * @param {string} identifier - The identifier to be sent to the negotiate endpoint.
 * @param {(message: SignalRMessage) => void} [onMessage] - Optional callback invoked when a SignalR message is received.
 *
 * @returns {Object} An object containing:
 *   - `connectionStarted` (boolean): Indicates if the SignalR connection has been established.
 *   - `message` (SignalRMessage | undefined): The latest message received from SignalR.
 *
 * @example
 * const { connectionStarted, message } = useSignalR({
 *   url: "/api/negotiate",
 *   identifier: userId,
 *   onMessage: (msg) => console.log(msg),
 * });
 */
function useSignalR({ url: negotiateUrl, identifier, onMessage }: SignalRProps) {
  const [message, setMessage] = useState<SignalRMessage>();
  const [connection, setConnection] = useState<null | signalR.HubConnection>(null); // State to hold the SignalR connection
  const [connectionStarted, setConnectionStarted] = useState(false); // Flag to track if the connection has been started

  useEffect(() => {
    if (!identifier) return console.warn("No identifier provided for SignalR connection.");

    const fetchNegotiateUrl = async () => {
      try {
        // Only fetch the negotiate URL if the connection doesn't exist
        if (!connection) {
          // Make a POST request to the negotiate endpoint
          const response = await httpService.post(negotiateUrl, { identifier: identifier });
          const { url, accessToken } = response;

          // Create the SignalR connection
          const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
              accessTokenFactory: () => accessToken,
            })
            .build();

          // Set the connection in the state
          setConnection(newConnection);
        }
      } catch (error) {
        console.error("Failed to negotiate:", error);
      }
    };

    fetchNegotiateUrl();
  }, [connection, identifier, negotiateUrl]); // Run the effect whenever the connection changes

  useEffect(() => {
    // Effect to start the connection and register event handlers
    if (connection && !connectionStarted) {
      const startConnection = async () => {
        try {
          await connection.start();
          if (process.env.NODE_ENV === "development") console.log("SignalR connection established.");

          // Dynamically create event handlers for each MessageType
          Object.keys(MessageType).forEach((type) => {
            connection.on(type, (data: SignalRMessage["message"]) => {
              const message = { type: type, message: data } as SignalRMessage;
              setMessage(message);
              if (onMessage) onMessage(message);
            });
          });

          // Update the flag to indicate that the connection has been started
          setConnectionStarted(true);
        } catch (error) {
          console.error("Failed to start SignalR connection:", error);
        }
      };

      startConnection();
    }
  }, [connection, connectionStarted, onMessage]); // Run the effect whenever the connection changes

  useEffect(() => {
    // Cleanup function to stop the connection when component unmounts
    return () => {
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        connection.stop();
        if (process.env.NODE_ENV === "development") console.log("SignalR connection stopped.");
      }
    };
  }, [connection]); // Run the cleanup function only once when the component unmounts

  return { connectionStarted, message };
}

export default useSignalR;
